const optimal = 'var(--colorHealthOptimal1Bg)';
const acceptable = 'var(--colorHealthAcceptable1Bg)';
const attention = 'var(--colorHealthAttention1Bg)';
const danger = 'var(--colorHealthDanger1Bg)';
const unknown = 'var(--colorHealthUnknown1Bg)';

const optimalLight = 'var(--colorHealthOptimal2Bg)';
const acceptableLight = 'var(--colorHealthAcceptable2Bg)';
const attentionLight = 'var(--colorHealthAttention2Bg)';
const dangerLight = 'var(--colorHealthDanger2Bg)';
const unknownLight = 'var(--colorHealthUnknown2Bg)';

export const nodeTypes = {
    company: 'COMPANY',
    plant: 'PLANT',
    group: 'GROUP',
    machine: 'MACHINE',
    measurementPoint: 'MEASUREMENT_POINT',
    sensor: 'SENSOR',
};

export const getNodeType = (nodeType: string) => {
    let type = 'node';
    if (isManagementNode(nodeType)) {
        type = 'management';
    } else if (isEquipmentNode(nodeType)) {
        type = 'equipment';
    }
    return type;
};

export const isManagementNode = (nodeType: string) => {
    const { group, company, plant } = nodeTypes;
    return nodeType === group || nodeType === company || nodeType === plant;
};

export const isEquipmentNode = (nodeType: string) => {
    const { machine, measurementPoint, sensor } = nodeTypes;
    return nodeType === machine || nodeType === measurementPoint || nodeType === sensor;
};

export const isMaleTermination = (nodeType: string) => {
    const { measurementPoint, sensor, group } = nodeTypes;
    return nodeType === measurementPoint || nodeType === sensor || nodeType === group;
};

export const isFemaleTermination = (nodeType: string) => {
    const { machine, company, plant } = nodeTypes;
    return nodeType === machine || nodeType === company || nodeType === plant;
};

export const hasChildrenType = (node: any, type: string) => {
    if (node?.children) {
        return node.children.some((child: any) => child.type === type);
    }
    return false;
};

export const hasParentTypeInPath = (node: any, type: string, treeMap: any) => {
    if (node?.path) {
        const path = node.path.split('/');
        return path.some((id: string) => treeMap[id]?.type === type);
    }
    return false;
};

export const isClientNode = (node: any, treeMap: any) => {
    return (
        node.type === nodeTypes.company ||
        (node.type === nodeTypes.group &&
            hasParentTypeInPath(node, nodeTypes.company, treeMap) &&
            !hasParentTypeInPath(node, nodeTypes.plant, treeMap))
    );
};

export const isProcessNode = (node: any, treeMap: any) => {
    return node.type === nodeTypes.group && hasParentTypeInPath(node, nodeTypes.plant, treeMap);
};

export const isPlantOrBelow = (node: any, treeMap: any) => {
    // `path` leads up to the node without including it, so the plant itself only matches by its own type.
    return node.type === nodeTypes.plant || hasParentTypeInPath(node, nodeTypes.plant, treeMap);
};

export const isTopLevelNode = (node: any) => {
    return node.id === 0 || node.parent_id === 0;
};

export const getTypeIdFromPath = (node: any, type: string, treeMap: any) => {
    if (node?.path) {
        const path = node.path.split('/');
        return path.find((id: string) => treeMap[id]?.type === type);
    }
    return null;
};

export const getOwnOrAncestorTypeId = (node: any, type: string, treeMap: any): number | null => {
    if (node?.type === type) {
        return node.id;
    }
    const id = getTypeIdFromPath(node, type, treeMap);
    return id ? Number(id) : null;
};

export const hasIdInPath = (node: any, id: string) => {
    return node?.path?.split('/').includes(id) ?? false;
};

export const machine_health_name: { [key: number]: string } = {
    0: 'Optimal',
    1: 'Acceptable',
    2: 'Attention',
    3: 'Danger',
    4: 'N/A',
};

/**
 * Colour for an asset inside a maintenance window. Not an entry in `machine_health_colors`: the asset
 * still has a real health, it just isn't reported while the window is open, so callers choose between
 * this and the health colour instead of looking it up by index.
 */
export const maintenance_color = unknown;
export const maintenance_color_light = unknownLight;

export const machine_health_colors: { [key: number]: string } = {
    0: optimal,
    1: acceptable,
    2: attention,
    3: danger,
    4: unknown,
};

export const machine_health_colors_light: { [key: number]: string } = {
    0: optimalLight,
    1: acceptableLight,
    2: attentionLight,
    3: dangerLight,
    4: unknownLight,
};
